import config from '../../../utils/config.js';
import { formatAsCurrency } from '../../../utils/formatAsCurrency';
import { WMSFetch } from '../../utils/common-api-calls';

export const getPendingRmas = async (currentUser) => {
    const pendingRmas = await WMSFetch('wms/rma/get-pending-rmas', {}, currentUser);
    return mapRmaGridRows(pendingRmas);
};

export const getHistoryRmas = async (currentUser) => {
    const historyRmas = await WMSFetch('wms/rma/get-rma-history', {}, currentUser);
    return mapRmaGridRows(historyRmas);
};

function mapRmaGridRows(rows) {
    return (Array.isArray(rows) ? rows : []).map((rmaRow) => {
        const createdDateSort = getCreatedDateSortValue(rmaRow.createdDate);
        const dateObject = createdDateSort ? new Date(createdDateSort) : null;
        const rmaCreatedDateSort = getCreatedDateSortValue(rmaRow.rmaCreatedDate);
        const rmaExpirationDateSort = getCreatedDateSortValue(rmaRow.rmaExpirationDate);
        const rmaCreatedDateObject = rmaCreatedDateSort ? new Date(rmaCreatedDateSort) : null;
        const rmaExpirationDateObject = rmaExpirationDateSort ? new Date(rmaExpirationDateSort) : null;

        return {
            ...rmaRow,
            createdDateDisplay: dateObject ? dateObject.toLocaleDateString() : '',
            createdDateSort,
            rmaCreatedDateDisplay: rmaCreatedDateObject ? rmaCreatedDateObject.toLocaleDateString() : '',
            rmaExpirationDateDisplay: rmaExpirationDateObject ? rmaExpirationDateObject.toLocaleDateString() : '',
            rmaCreatedDateSort,
            rmaExpirationDateSort,
            rmaTotalDisplay: Number(rmaRow.rmaTotal || 0).toLocaleString('en-US', {
                style: 'currency',
                currency: 'USD',
            }),
        };
    });
}

export const doesRmaMatchTableFilters = (rmaRow, searchText, isHistory, historyFilters, historyRmaStatusFilters) => {
    if (searchText) {
        const normalizedSearch = searchText.toUpperCase();
        const fieldsToSearch = [
            rmaRow.rmaName,
            rmaRow.rmaNumber,
            rmaRow.customerPoNumber,
            rmaRow.assignedToName,
            rmaRow.customerName,
            rmaRow.contactName,
            rmaRow.invoiceName,
            rmaRow.salesOrderName,
            rmaRow.releaseName,
        ];

        const matchesSearch = fieldsToSearch.some((fieldValue) =>
            String(fieldValue || '')
                .toUpperCase()
                .includes(normalizedSearch)
        );

        if (!matchesSearch) return false;
    }

    if (!isHistory) return true;

    const matchesSubmissionStatus = historyFilters.length === 0 || historyFilters.includes(rmaRow.submissionStatus);
    const rmaStatusValue = rmaRow.rmaStatus || 'N/A';
    const matchesRmaStatus = historyRmaStatusFilters.length === 0 || historyRmaStatusFilters.includes(rmaStatusValue);

    return matchesSubmissionStatus && matchesRmaStatus;
};

export const getRmaReviewData = async (rmaId, currentUser) => {
    const rmaReviewData = await WMSFetch('wms/rma/get-rma-review-data', { rmaId }, currentUser);

    if (!rmaReviewData) return null;

    return {
        ...rmaReviewData,
        lines: mapRmaLines(rmaReviewData.lines),
    };
};

export const getHistoryRmaDetail = async (rmaId, currentUser) => {
    const data = await WMSFetch('wms/rma/get-rma-history-detail', { rmaId }, currentUser);

    if (!data) return null;

    return {
        ...data,
        lines: mapRmaLines(data.lines),
    };
};

function mapRmaLines(lines) {
    return lines?.map((line) => {
        const quantityToReturn = Number(line.quantityToReturn) || 0;
        const linePrice = Number(line.linePrice || 0);
        const invoiceLinePrice =
            Number(line.invoiceLinePrice) || (quantityToReturn > 0 ? linePrice / quantityToReturn : 0);

        return {
            ...line,
            invoiceLinePrice,
            quantityToReturn,
            linePrice,
            linePriceDisplay: formatAsCurrency(linePrice),
            pricePaidDisplay: formatAsCurrency(invoiceLinePrice),
        };
    });
}

export const saveRmaReview = async (rmaId, reviewForm, currentUser) => {
    return await processRmaReviewAction('save', rmaId, reviewForm, currentUser);
};

export const releaseRmaReview = async (rmaId, currentUser) => {
    return await processRmaReviewAction('release', rmaId, {}, currentUser);
};

export const denyRma = async (rmaId, reviewForm, currentUser) => {
    return await processRmaReviewAction('deny', rmaId, reviewForm, currentUser);
};

export const approveRma = async (rmaId, reviewForm, currentUser) => {
    return await processRmaReviewAction('approve', rmaId, reviewForm, currentUser);
};

export const submitRmaToSales = async (rmaId, reviewForm, currentUser) => {
    return await processRmaReviewAction('submitToSales', rmaId, reviewForm, currentUser);
};

/**
 * Same Dropbox path + multer rules as public RMA submit (field name `attachments`).
 */
export async function uploadRmaSubmissionDocuments(rmaId, attachmentFiles, currentUser) {
    const formData = new FormData();
    formData.append(
        'authentication',
        JSON.stringify({
            userId: currentUser.authentication.userId,
            userEmail: currentUser.authentication.userEmail,
            userCompany: currentUser.authentication.userCompany || localStorage.getItem('company'),
        })
    );
    formData.append('rmaId', String(rmaId));
    for (const file of attachmentFiles || []) {
        formData.append('attachments', file);
    }

    const response = await fetch(`${config.origin}/wms/rma/upload-submission-documents`, {
        method: 'POST',
        body: formData,
    });

    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
        throw new Error(data?.message || data?.error || `Upload failed (${response.status})`);
    }
    if (data?.success === false) {
        throw new Error(data?.message || 'Upload was not successful.');
    }
    return data;
}

async function processRmaReviewAction(actionType, rmaId, reviewForm, currentUser) {
    return await WMSFetch('wms/rma/rma-review-action', { actionType, rmaId, reviewForm }, currentUser);
}

function getCreatedDateSortValue(createdDate) {
    if (!createdDate) return 0;

    // Salesforce often returns timezone offsets like +0000; normalize to +00:00 for stable browser parsing.
    const normalizedDate = String(createdDate).replace(/([+-]\d{2})(\d{2})$/, '$1:$2');
    const parsedTimestamp = Date.parse(normalizedDate);

    return Number.isNaN(parsedTimestamp) ? 0 : parsedTimestamp;
}
